import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Waves, Cpu, Shield, Wrench } from "lucide-react";
import { HERO, DIV } from "@/constants/testIds";
import { DIVISIONS, GROUP_STATS } from "@/data/divisions";

const HERO_IMG = "https://images.pexels.com/photos/30475898/pexels-photo-30475898.jpeg";

export default function Home() {
  return (
    <div className="text-white">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/60 via-[#060A14]/70 to-[#060A14]" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="grain" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
          <div className="max-w-4xl reveal">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-[#FF5722]" />
              <span className="eyebrow">01 · Billtech Holdings Global</span>
            </div>
            <h1 className="font-display font-black text-white text-[44px] md:text-[68px] lg:text-[84px] leading-[0.95] tracking-tighter">
              Integrated Marine &<br />
              <span className="text-[#FF5722]">Engineering Infrastructure</span><br />
              for Global Offshore Energy.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed">
              Billtech Holdings drives the full lifecycle of offshore assets through its Marine
              &amp; Engineering Group. Across eight specialized subsidiaries and 100+ micro-entities,
              we deliver end-to-end technical engineering, asset integrity, advanced marine
              logistics and subsea intervention — under one unified global QHSE standard.
            </p>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/divisions" className="btn-primary" data-testid={HERO.exploreBtn}>
                Explore Our Divisions <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid={HERO.rfqBtn}>
                Submit Project RFQ
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-[#94A3B8] font-mono text-[10px] uppercase tracking-[0.3em]">
          <span>Scroll</span>
          <div className="w-px h-10 bg-white/30" />
        </div>
      </section>

      {/* CAPABILITIES BAR */}
      <section className="border-y border-white/10 bg-[#0C1322]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <StatBlock number={GROUP_STATS.divisions.toString()} label="Specialized Operating Divisions" />
          <StatBlock number={`${GROUP_STATS.services}+`} label="Integrated Offshore Services" />
          <StatBlock number={GROUP_STATS.qhse} label="QHSE Incident Record" />
          <StatBlock number={GROUP_STATS.mobilization} label="Global Mobilization" />
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">02 · Group Capabilities</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] tracking-tight">
              Full-lifecycle offshore infrastructure, under one flag.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="text-white/70 text-lg leading-relaxed">
              From the first bathymetric survey to permanent well plugging, the Billtech Holdings
              Marine &amp; Engineering Group covers
              every touchpoint of the offshore asset lifecycle. Our subsidiaries operate as
              specialists — micro-branded, liability-isolated, and technically deep — while
              deploying under a single unified operational and safety standard.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PillarCard icon={<Cpu size={22} />} title="Digital & Tech" body="Digital twins, cyber, telemetry, VSAT." />
          <PillarCard icon={<Waves size={22} />} title="Subsea & Well" body="Seabed mapping, well support, cementing." />
          <PillarCard icon={<Wrench size={22} />} title="Asset Integrity" body="Mechanical, structural & rotating equipment." />
          <PillarCard icon={<Shield size={22} />} title="QHSE & Salvage" body="Compliance, environment, decommissioning." />
        </div>
      </section>

      {/* DIVISIONS BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-4">03 · Operating Divisions</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] tracking-tight max-w-2xl">
              Eight divisions. One hundred micro-subsidiaries.
            </h2>
          </div>
          <Link to="/divisions" className="btn-secondary" data-testid="home-view-all-divisions">
            View all divisions <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 auto-rows-[220px] gap-4">
          {DIVISIONS.map((d, idx) => (
            <DivisionBentoCard key={d.id} d={d} idx={idx} />
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative border-y border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF5722] via-[#F4511E] to-[#FF5722]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 mb-2">
              Global Tender Portal
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight">
              Ready to move a project offshore?
            </h3>
          </div>
          <Link
            to="/contact"
            className="bg-[#060A14] text-white px-8 py-4 font-display font-bold uppercase tracking-wider text-sm hover:bg-black transition-colors inline-flex items-center gap-2"
            data-testid="cta-submit-rfq"
          >
            Submit RFQ <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatBlock({ number, label }) {
  return (
    <div className="border-l-2 border-[#FF5722] pl-4">
      <div className="font-display text-4xl md:text-5xl text-white font-black tracking-tighter">{number}</div>
      <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#94A3B8]">{label}</div>
    </div>
  );
}

function PillarCard({ icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#0C1322] p-6 hover:border-[#FF5722] transition-colors group">
      <div className="w-11 h-11 flex items-center justify-center bg-[#131D33] text-[#FF5722] group-hover:bg-[#FF5722] group-hover:text-white transition-colors mb-5">
        {icon}
      </div>
      <div className="font-display text-lg font-bold text-white mb-2">{title}</div>
      <div className="text-sm text-[#94A3B8] leading-relaxed">{body}</div>
    </div>
  );
}

function DivisionBentoCard({ d, idx }) {
  // Asymmetrical span pattern
  const spans = [
    "md:col-span-3 lg:col-span-5 md:row-span-2",
    "md:col-span-3 lg:col-span-4",
    "md:col-span-3 lg:col-span-3",
    "md:col-span-3 lg:col-span-3",
    "md:col-span-3 lg:col-span-4 md:row-span-2",
    "md:col-span-3 lg:col-span-5",
    "md:col-span-3 lg:col-span-4",
    "md:col-span-6 lg:col-span-3",
  ];
  return (
    <Link
      to={`/divisions#${d.id}`}
      data-testid={DIV.card(d.id)}
      className={`relative overflow-hidden border border-white/10 bg-[#0C1322] group hover:border-[#FF5722] transition-all ${spans[idx] || "md:col-span-3 lg:col-span-3"}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ backgroundImage: `url(${d.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#060A14] via-[#060A14]/80 to-transparent" />
      <div className="relative h-full p-6 flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722]">
            Div · {d.code}
          </span>
          <ArrowRight size={16} className="text-white/40 group-hover:text-[#FF5722] group-hover:translate-x-1 transition-all" />
        </div>
        <div>
          <div className="font-display text-lg lg:text-xl font-bold text-white leading-tight tracking-tight">
            {d.name.replace("Billtech ", "")}
          </div>
          <div className="mt-2 text-xs text-[#94A3B8] line-clamp-2">{d.tagline}</div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
            {d.subsidiaries.length} entities
          </div>
        </div>
      </div>
    </Link>
  );
}
